/* eslint-disable arrow-parens */
import testsApi from '@/api/tests';

// initial state
const state = {
  tests: [],
  students: [],
  selectedTest: null,
  currentTest: null,
  testResult: null,
  testGroups: [],
  testsOnPage: [],
  testsOnPageError: null,
  classificationLegendOpen: false,
  classificationLegendCategory: null,
  classCultureLegendOpen: false,
  testResultDetailsOpen: false,
  testResultDetailsOpenNew: false,
  extendTestModalOpen: false,
  testResultDetails: null,
  testsPaging: {
    page: 1,
    pageSize: 20,
    totalDataCount: 0,
  },
};

// getters
const getters = {
  tests: (currentState) => currentState.tests,
  pagedTests: (currentState) => currentState.testsOnPage,
  pagedTestsError: (currentState) => currentState.testsOnPageError,
  testsPaging: (currentState) => currentState.testsPaging,
  testStudents: (currentState) => currentState.students,
  selectedTest: (currentState) => currentState.selectedTest,
  currentTest: (currentState) => currentState.currentTest,
  testResult: (currentState) => currentState.testResult,
  testGroups: (currentState) => currentState.testGroups,
  classificationLegendOpen: (currentState) =>
    currentState.classificationLegendOpen,
  classificationLegendCategory: (currentState) =>
    currentState.classificationLegendCategory,
  classCultureLegendOpen: (currentState) => currentState.classCultureLegendOpen,
  testResultDetailsOpen: (currentState) => currentState.testResultDetailsOpen,
  testResultDetailsOpenNew: (currentState) =>
    currentState.testResultDetailsOpenNew,
  extendTestModalOpen: (currentState) => currentState.extendTestModalOpen,
  testResultDetails: (currentState) => currentState.testResultDetails,
  testsHasNextPage: (currentState) =>
    currentState.testsPaging.page * currentState.testsPaging.pageSize <
    currentState.testsPaging.totalDataCount,
  testsHasPreviousPage: (currentState) => currentState.testsPaging.page > 1,
  testsTotalPages: (currentState) =>
    Math.ceil(
      currentState.testsPaging.totalDataCount /
        currentState.testsPaging.pageSize,
    ),
};

// actions
const actions = {
  getTestStudents({ dispatch, commit }, test) {
    dispatch('showLoader');
    testsApi.getStudents(test).then((response) => {
      commit('setTestStudents', { students: response.data });
      dispatch('hideLoader');
    });
  },
  getPagedTests({ dispatch, commit }, paging) {
    dispatch('showLoader');
    testsApi
      .getPagedTests(paging)
      .then((response) => {
        commit('setPagedTestsError', null);
        commit('setPagedTests', {
          tests: response.data.data,
          paging: {
            page: response.data.page,
            pageSize: response.data.pageSize,
            totalDataCount: response.data.totalDataCount,
          },
        });
        dispatch('hideLoader');
      })
      .catch((error) => {
        commit(
          'setPagedTestsError',
          `An error occurred while trying to retrieve the list of reports (${error.response.statusText})`,
        );
        commit('setPagedTests', {
          tests: [],
          paging: { ...paging, page: 1, totalDataCount: 0 },
        });
        dispatch('hideLoader');
      });
  },
  setCurrentTest({ commit }, test) {
    commit('setCurrentTest', test);
  },
  setTestResultDetails({ commit }, details) {
    commit('setTestResultDetails', details);
  },
  setTestResult({ commit }, testResult) {
    commit('setTestResult', testResult);
  },
  showTestResultDetails({ commit }) {
    commit('setTestResultDetailsOpen', true);
  },
  hideTestResultDetails({ commit }) {
    commit('setTestResultDetailsOpen', false);
  },
  showTestResultDetailsNew({ commit }) {
    commit('setTestResultDetailsOpenNew', true);
  },
  hideTestResultDetailsNew({ commit }) {
    commit('setTestResultDetailsOpenNew', false);
  },
  showClassificationLegend({ commit }, category) {
    commit('setClassificationLegendOpen', true);
    commit('setClassificationLegendCategory', category);
  },
  hideClassificationLegend({ commit }) {
    commit('setClassificationLegendOpen', false);
  },
  showClassCultureLegend({ commit }) {
    commit('setClassCultureLegendOpen', true);
  },
  hideClassCultureLegend({ commit }) {
    commit('setClassCultureLegendOpen', false);
  },
  showExtendTestModal({ commit }) {
    commit('setExtendTestModalOpen', true);
  },
  hideExtendTestModal({ commit }) {
    commit('setExtendTestModalOpen', false);
  },
  getTests({ dispatch, commit }) {
    dispatch('showLoader');
    testsApi.getTests().then((response) => {
      commit('setTests', { tests: response.data });
      dispatch('hideLoader');
    });
  },
  getTestsMinimum({ dispatch, commit }) {
    dispatch('showLoader');
    testsApi.getTestsMinimum().then((response) => {
      commit('setTests', { tests: response.data });
      dispatch('hideLoader');
    });
  },
  selectTest({ commit }, test) {
    commit('selectTest', { test });
  },
  getTest({ dispatch, commit }, id) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.getTest(id).then(
        (response) => {
          commit('selectTest', response.data);
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
  removeTest({ dispatch }, id) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.removeTest(id).then(
        (response) => {
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
  getTestResult({ dispatch }, params) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.getTestResult(params.id, params.size).then(
        (response) => {
          // commit('setTestResult', response.data);
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
  startTest({ dispatch }, test) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.startTest(test).then(
        (response) => {
          dispatch('selectTest', {
            id: response.data.id,
            code: response.data.code,
          });
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
  createTest({ dispatch }, test) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.createTest(test).then(
        (response) => {
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
  extendTest({ dispatch }, test) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.extendTest(test).then(
        (response) => {
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
  resetTest({ dispatch }, test) {
    return new Promise((resolve, reject) => {
      dispatch('showLoader');
      testsApi.resetTest(test).then(
        (response) => {
          dispatch('hideLoader');
          resolve(response);
        },
        (error) => {
          dispatch('hideLoader');
          reject(error);
        },
      );
    });
  },
};

// mutations
const mutations = {
  setTests(currentState, { tests }) {
    currentState.tests = tests;
  },
  setPagedTests(currentState, { tests, paging }) {
    currentState.testsOnPage = tests;
    currentState.testsPaging = paging;
  },
  setTestStudents(currentState, { students }) {
    currentState.students = students;
  },
  selectTest(currentState, test) {
    currentState.selectedTest = test;
  },
  setTestResult(currentState, test) {
    currentState.testResult = test;
  },
  setGroups(currentState, { groups }) {
    currentState.testGroups = groups;
  },
  setTestResultDetails(currentState, details) {
    currentState.testResultDetails = details;
  },
  setClassificationLegendOpen(currentState, isOpen) {
    currentState.classificationLegendOpen = isOpen;
  },
  setClassificationLegendCategory(currentState, category) {
    currentState.classificationLegendCategory = category;
  },
  setClassCultureLegendOpen(currentState, isOpen) {
    currentState.classCultureLegendOpen = isOpen;
  },
  setTestResultDetailsOpen(currentState, isOpen) {
    currentState.testResultDetailsOpen = isOpen;
  },
  setTestResultDetailsOpenNew(currentState, isOpen) {
    currentState.testResultDetailsOpenNew = isOpen;
  },
  setExtendTestModalOpen(currentState, isOpen) {
    currentState.extendTestModalOpen = isOpen;
  },
  setCurrentTest(currentState, test) {
    currentState.currentTest = test;
  },
  setPagedTestsError(currentState, error) {
    currentState.testsOnPageError = error;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/tests.js